/**
 * Pure helpers for the Settings side-tab list.
 *
 * Kept free of React so the gating rule for the SaaS-only "Plans & Billing"
 * tab can be unit-tested without rendering ``Settings``.
 */

export const BILLING_TAB = { id: 'billing', label: 'Plans & Billing' };

/** God-only review queue for Creator requests filed through ``/waitlist``. */
export const WAITING_LIST_TAB = { id: 'waiting-list', label: 'Waiting List' };

/**
 * Workspace logo + name editor (Figma 9244:4940). Always the first tab, for
 * workspace Admins and gods only; it edits the active workspace through
 * ``PATCH /workspaces/{id}``, which the backend gates the same way.
 */
export const WORKSPACE_TAB = { id: 'workspace', label: 'Workspace' };

/** ``/settings?tab=<tab>`` plus the usecase id when one is active. */
export function settingsTabPath(tab, usecaseId) {
    const params = new URLSearchParams({ tab });
    if (usecaseId) params.set('usecaseId', usecaseId);
    return `/settings?${params.toString()}`;
}

/** Tab the billing entry is inserted directly after. */
const BILLING_ANCHOR_TAB_ID = 'providers';

/** Tab the waiting-list entry is inserted directly after. */
const WAITING_LIST_ANCHOR_TAB_ID = 'users';

/**
 * Insert ``tab`` right after ``anchorId`` when ``show`` holds, otherwise strip
 * any stale copy of it. Appends when the anchor is missing; never duplicates
 * (an existing copy is replaced in place, so a relabelled tab is refreshed).
 */
function withGatedTab(items, tab, anchorId, show) {
    if (!show) {
        return items.filter((item) => item.id !== tab.id);
    }
    if (items.some((item) => item.id === tab.id)) {
        return items.map((item) => (item.id === tab.id ? tab : item));
    }
    const anchorIndex = items.findIndex((item) => item.id === anchorId);
    if (anchorIndex === -1) {
        return [...items, tab];
    }
    return [
        ...items.slice(0, anchorIndex + 1),
        tab,
        ...items.slice(anchorIndex + 1),
    ];
}

/**
 * ``Waiting List (N)`` — the sidebar label once the pending-request count is
 * known, so a god sees whether anyone is waiting without opening the tab
 * (the same string pattern ``Settings`` uses for ``Model Providers (N)``;
 * Spark's tabs have no badge slot). Anything but a non-negative integer —
 * ``null`` while loading, after a failed fetch, or for a non-god — leaves the
 * bare label, so the tab never flashes a stale or made-up number.
 */
export function waitingListTabLabel(pendingCount) {
    if (Number.isInteger(pendingCount) && pendingCount >= 0) {
        return `${WAITING_LIST_TAB.label} (${pendingCount})`;
    }
    return WAITING_LIST_TAB.label;
}

/**
 * Return the visible tab list.
 *
 * ``tabItems`` is the already role-filtered list (api-keys / users mapping
 * applied by ``Settings``). The billing tab is inserted right after the
 * ``providers`` tab only when all three hold: the platform runs as SaaS,
 * billing is enabled on the backend, and the current user is an Admin.
 * Otherwise the list is returned unchanged, so self-hosted installs never
 * see (or lazy-load) the Stripe-backed section.
 *
 * The waiting-list tab is inserted right after ``users`` only for ``isGod``
 * callers (platform role ``god``, or any user when auth is disabled —
 * mirroring the backend ``require_god`` bypass) and only on SaaS: the
 * waiting list does not exist on self-hosted installs, where strangers are
 * invited by a workspace admin instead (the backend answers 404 there). Its
 * label carries the pending count (``waitingListPendingCount``) when one is
 * known — see ``waitingListTabLabel``.
 *
 * The workspace tab is prepended (always first, on every cloud) for
 * workspace Admins and gods — the two roles the backend lets edit a
 * workspace. Editors and Viewers never see it.
 */
export function getVisibleSettingsTabs({
    tabItems,
    isAdmin,
    isSaas,
    billingEnabled,
    isGod = false,
    waitingListPendingCount = null,
}) {
    const items = Array.isArray(tabItems) ? tabItems : [];
    const showBilling = isSaas === true && billingEnabled === true && isAdmin === true;
    const showWaitingList = isGod === true && isSaas === true;
    const showWorkspace = isAdmin === true || isGod === true;
    const withBilling = withGatedTab(items, BILLING_TAB, BILLING_ANCHOR_TAB_ID, showBilling);
    const waitingListTab = { ...WAITING_LIST_TAB, label: waitingListTabLabel(waitingListPendingCount) };
    const withWaitingList = withGatedTab(withBilling, waitingListTab, WAITING_LIST_ANCHOR_TAB_ID, showWaitingList);
    const withoutWorkspace = withWaitingList.filter((item) => item.id !== WORKSPACE_TAB.id);
    return showWorkspace ? [WORKSPACE_TAB, ...withoutWorkspace] : withoutWorkspace;
}
